/*
 * Copyright (c) 2023-2025 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect, useCallback } from "react";
import { AccountContext } from "../context/ContextProvider";
import { Input, Label, PopoverBody, Table } from "reactstrap";
import { downloadArrayAsCSV, formatBytes } from "../util/File";
import { secondsToTimeStringSession, secondsToTimeStringShort, unixEpochToString } from "../util/DateTime";
import ScrollableDiv from "./ScrollableDiv";
import DismissablePopover from "./DismissablePopover";
import useUpdateState from "../util/useUpdateState";

const Paging = ({totalPages, currentPage, onPageChange, margins='1', loading}) => {
  const [state, , updateState] = useUpdateState({ loadingText: null });

  const getPageNumbers = () => {
    const maxPages = 10; // Maximum pages to display
    const halfRange = Math.floor(maxPages / 2);

    let start = Math.max(1, currentPage - halfRange);
    let end = Math.min(totalPages, currentPage + halfRange);

    if (end - start < maxPages - 1) {
      if (currentPage < halfRange + 1) {
        end = Math.min(totalPages, start + maxPages - 1);
      } else if (currentPage > totalPages - halfRange) {
        start = Math.max(1, end - maxPages + 1);
      }
    }

    // Generate the array of page numbers
    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  };

  useEffect(() => {
    if (!loading) {
      updateState({ loadingText: null });
      return;
    } else {
      // const loadingTexts = ['Loading... ', 'Loading ...', 'Loading. ..', 'Loading.. .', 'Loading. ..', 'Loading ...'];
      // const loadingTexts = ['Loading...   ', 'Loading ...  ', 'Loading  ... ', 'Loading   ...'];
      const loadingTexts = ['Loading.  ', 'Loading.. ', 'Loading...', 'Loading   '];
      let currentLoadingIndex = 0;
      updateState({ loadingText: loadingTexts[currentLoadingIndex] });
      
      const timer = setInterval(() => {
        currentLoadingIndex = (currentLoadingIndex + 1) % loadingTexts.length;
        updateState({loadingText: loadingTexts[currentLoadingIndex]})
      }, 500)
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[loading]);

  if (state.loadingText) {
    return (
      <div className="mt-3 d-flex justify-content-center align-items-center">
        <Label className={`px-${margins} text-center`} /*size="sm"*/>&lt;</Label>
        <Label className={`px-${margins} text-center`} style={{ whiteSpace: "pre" }} /*size="sm"*/>{state.loadingText}</Label>
        <Label className={`px-${margins} text-center`} /*size="sm"*/>&gt;</Label>
      </div>
    ); 
  }

  const pageNumbers = getPageNumbers();
  const showFirst = pageNumbers.length > 0 && pageNumbers[0] !== 1;
  const showLast = pageNumbers.length > 0 && pageNumbers[pageNumbers.length - 1] !== totalPages;
  return (
    <div className="mt-3 d-flex justify-content-center align-items-center">
      {currentPage === 1 ? (
        <Label
          className={`px-${margins} text-center`}
          // size="sm"
        >
          &lt;
        </Label>
      ) : (
        <Label
          className={`px-${margins} ${currentPage === 1 ? '' : 'link-primary' } clickable text-center`}
          // size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lt;
        </Label>
      )}

      {showFirst && 
        <div className="d-flex">
          <Label className={`px-${margins} link-primary clickable text-center`} /*size="sm"*/ onClick={() => onPageChange(1)} >1</Label>
          <Label className={`px-${margins} text-center`} /*size="sm"*/> ... </Label>
        </div>
      }
      
      {getPageNumbers().map(page => (
        <Label
          className={`px-${margins} ${page === currentPage ? "" : "link-primary"} clickable text-center`}
          // size="sm"
          onClick={() => onPageChange(page)}
        >
          {page}
        </Label>
      ))}

      {showLast &&
        <div className="d-flex">
          <Label className={`px-${margins} text-center`} /*size="sm"*/> ... </Label>
          <Label className={`px-${margins} link-primary clickable text-center`} /*size="sm"*/ onClick={() => onPageChange(totalPages)} >{totalPages}</Label>
        </div>
      }
      {(currentPage >= totalPages) ? (
        <Label
          className={`px-${margins} text-center`}
          // size="sm"
        >
          &gt;
        </Label>
      ) : (
        <Label
          className={`px-${margins} ${currentPage === totalPages ? '' : 'link-primary' } clickable text-center`}
          // size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &gt;
        </Label>
      )}
      
    </div>
  ); 
}

const SortableTable = ({
  data,
  columns,
  filter,
  filterEnabled = true,
  initialSortKey,
  initialSortAscend = false,
  placeholder,
  downloadButtonId,
  downloadKeys = null,
  selectKey = "id",
  selectValue,
  onSelected,
  paging = false,
  currentPage = 1,
  onPageChanged = null,
  itemsPerPage = 50,
  noScroll = false,
  checked = null,
  onChecked = null,
  checkedAll = null,
  onCheckedAll = null,
  tableName = "",
}) => {
  // context
  const accountContext = useContext(AccountContext);

  // state
  const initialFilter = {};
  columns.filter(column => column.type === "enum" || column.type === "object.enum" || column.type  === "list").forEach(column => {
    if (!column.enum) {
      return;
    }
    const columnFilters = column.unknownEnum ? { [null]: true } : {};
    Object.keys(column.enum).forEach(key => columnFilters[key] = true);
    initialFilter[column.id] = columnFilters;
  });

  const initialState = {
    sortId: initialSortKey ?? columns[0].id,
    sortDesc: initialSortAscend,
    enumFilter: initialFilter,
    enumFilterPopover: null,
    selectedItem: null,
    loadingText: null,
  };
  const [state, , updateState] = useUpdateState(initialState);

  // add on click to download button
  const handleDownload = useCallback(() => {
    const sortedData = parseData(data);

    let csvTitles;
    let csvData;

    if (downloadKeys) {
      csvTitles = downloadKeys.map(item => item.name);
      csvData = sortedData.map(sortedData => downloadKeys.map(c => convertDownloadKeyValue(c, sortedData[c.id])));
    } else {
      csvTitles = columns.map(column => column.name);
      csvData = sortedData.map(sortedData => columns.map(c => convertValue(c.id, sortedData[c.id], c.type)));
    }

    downloadArrayAsCSV(`${accountContext.clientSummary?.name ?? "data"}.csv`, [csvTitles, ...csvData]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, data, columns, filter]);

  useEffect(() => {
    if (downloadButtonId == null) {
      return;
    }

    // get button from id
    const buttonRef = document.getElementById(downloadButtonId);
    
    // add callback
    buttonRef.addEventListener("click", handleDownload);
    return () => buttonRef.removeEventListener("click", handleDownload);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadButtonId, handleDownload]);

  useEffect(() => {
    // If columns change, update enum list
    columns.filter(column => column.type  === "list").forEach(column => {
      if (!column.enum) {
        return;
      }

      const columnFilters = column.unknownEnum ? { [null]: (state.enumFilter?.[column.id]?.[null] ?? true) } : {};
      Object.keys(column.enum).forEach(key => {
        columnFilters[key] = state.enumFilter?.[column.id]?.[key] ?? true
      });
      
      const updateFilter = {...state.enumFilter }
      updateFilter[column.id] = columnFilters;
      updateState({ enumFilter: updateFilter });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[columns]);

  // helpers
  const defaultRender = ({index, value}) => (
    <td key={`${tableName}${index}-default`} className={value ? "" : "text-muted"}>{value ? value : "-"}</td>
  );

  const convertValue = (id, value, type) => {
    switch (type) {
      case "enum": return columns.find(column => column.id === id)?.enum?.[value] ?? "Unknown";
      case "object.enum":
        return { status: value.status, value: columns.find(column => column.id === id)?.enum?.[value.value] ?? "Unknown"};
      case "time": return unixEpochToString(value);
      case "time.short": return secondsToTimeStringShort(value);
      case "time.session": return secondsToTimeStringSession(value);
      case "bytes": return formatBytes(value);
      default: return value;
    }
  };

  const convertDownloadKeyValue = (downloadKey, value) => {
    switch (downloadKey.type) {
      case "enum": return downloadKey?.enum?.[value] ?? "Unknown";
      case "object.enum":
        return { status: value.status, value: downloadKey?.enum?.[value.value] ?? "Unknown"};
      case "time": return unixEpochToString(value);
      case "time.short": return secondsToTimeStringShort(value);
      case "time.session": return secondsToTimeStringSession(value);
      case "bytes": return formatBytes(value);
      case "bool": return value.toString();
      default: return value;
    }
  }

  const sortStringAsc = (itemA, itemB) => {
    let a = itemA?.length ? itemA.toLowerCase() : null;
    let b = itemB?.length ? itemB.toLowerCase() : null;
    let ret = 0;
    if (a == null && b != null) return -1;
    else if (a != null && b == null) return -1;
    else if (a > b) ret = 1;
    else if (a < b) ret = -1;
    return ret ;
  }

  const parseData = (data) => {
    // prepare filter process
    let filteredData = data;
    const activeColumn = columns.find(column => column.id === state.sortId);

    // Value to force to bottom of the list
    const excludeSort = activeColumn.excludeSort ?? null;

    // filter enumerations
    filteredData = filteredData.filter(data => {
      return Object.keys(state.enumFilter).reduce((acc, id) => {
        let value = data[id]?.value ?? data[id];
        const filter = state.enumFilter[id];
        
        if (Array.isArray(value)) {
          // Convert list to enum value
          value = value.sort((itemA, itemB) => sortStringAsc(itemA, itemB)).join();
        }
        return acc && Object.keys(filter).reduce((acc, key) => {
          if (!acc) return false;
          return filter[key] || (key === "null" ? filter[value] != null : value !== key);
        }, true);
      }, true);
    });

    // filter search string
    if (filter?.string) {
      const filterString = filter.string.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
      let filterKeys = filter.columns ?? columns.filter(column => column.type === "string" || column.type === "object.string").map(column => column.id);
      filterKeys = [ ...filterKeys, ...(filter?.filterKeys ?? []) ];
      filteredData = filteredData.filter(data => {
        return filterKeys.some(key => data[key] && (data[key].value ?? data[key]).toString().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").includes(filterString));
      });
    }

    // sort data
    const sortItem = (a, b) => {
      let ret = 0;
      if (a == null && b != null) return 1;
      else if (a != null && b == null) return -1;
      else if (a > b) ret = 1;
      else if (a < b) ret = -1;
      return ret * (state.sortDesc ? 1 : -1);
    };

    const sortString = (a, b) => {
      // If needed, apply to sort item
      let itemA = a?.length ? a.toLowerCase() : null;
      let itemB = b?.length ? b.toLowerCase() : null;

      // If an item matches excludeSort, change to null to sort to bottom
      if (excludeSort != null && itemA === excludeSort.toLowerCase()) {
        itemA = null;
      }

      if (excludeSort != null && itemB === excludeSort.toLowerCase()) {
        itemB = null;
      }

      return sortItem(itemA, itemB);
    };

    const sortList = (aList, bList) => {
      const a = aList?.length ? aList[0] : null;
      const b = bList?.length ? bList[0] : null;
      return sortString(a, b);
    };

    const sortObjectString = (a, b) => {
      const itemA = a?.value.length ? a.value.toLowerCase() : null;
      const itemB = b?.value.length ? b.value.toLowerCase() : null;
      return sortItem(itemA, itemB);
    }

    const activeEnum = activeColumn.enum;
    const sortEnum = (a, b) => {
      return sortItem(activeEnum[a], activeEnum[b]);
    };

    const sortEnumObject = (a, b) => {
      return sortItem(activeEnum[a.value], activeEnum[b.value]);
    };

    let sortHandler;
    switch (activeColumn.type) {
      case "string": sortHandler = sortString; break;
      case "number": sortHandler = sortItem; break;
      case "enum": sortHandler = sortEnum; break;
      case "object.string": sortHandler = sortObjectString; break;
      case "object.enum": sortHandler = sortEnumObject; break;
      case "list": sortHandler = sortList; break;
      default: sortHandler = sortItem; break;
    }

    return filteredData.sort((a, b) => sortHandler(a[state.sortId], b[state.sortId]));
  };

  // sorted data
  const sortedData = parseData(data);
  let displayData = sortedData;

  // Paging
  const totalPages = sortedData != null ? Math.ceil(sortedData.length / itemsPerPage) : 0;
  if (paging) {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    displayData = sortedData.slice(startIndex, endIndex);
  } 

  return (
    <ScrollableDiv className="px-2" noScroll={noScroll}>
      {paging && <Paging totalPages={totalPages} currentPage={currentPage} onPageChange={(page) => onPageChanged?.(page)} margins="2" loading={!filterEnabled}/> }
      <Table className="no-wrap mt-3 align-middle" responsive borderless >
        <thead>
          <tr>
            {checked && 
              <th key={`${tableName}-check-all-column`}>
                <div className="d-flex">
                  <Input
                    className
                    type="checkbox"
                    checked={checkedAll}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      onCheckedAll?.(checked);
                    }}
                  />
                </div>
              </th>
            }
            {columns.map((item, index) => {
              // sort
              const isStatic = item.type === "static";
              const sortSelected = state.sortId === item.id;
              const sortIcon = sortSelected ? (state.sortDesc ? "arrow-down" : "arrow-up") : "filter";

              // enum filter
              const filterKey = item.id;
              const filter = state.enumFilter[filterKey];
              const filterActive = filter != null && !Object.keys(filter).reduce((acc, item) => acc && filter[item], true);
              const popoverOpen = state.filterPopover === item.id;
              let enumKeys = null;

              if (item.type === "enum" || item.type === "object.enum" || item.type === "list") {
                enumKeys = Object.keys(item.enum);
                if (item.type === "list") {
                  enumKeys = enumKeys.sort((a, b) => sortStringAsc(a, b) )
                }

                if (item.unknownEnum) {
                  enumKeys.push(null);
                }
              }

              // other
              const selected = sortSelected;
              
              return (
                <th key={`${tableName}${index}-column`}>
                  <div className="d-flex">
                    <Label
                      className={`no-spacing no-select fw-bold ${isStatic || !filterEnabled ? "" : "clickable"} ${selected && filterEnabled ? "link-success" : "link-secondary" }`}
                      onClick={isStatic || !filterEnabled ? null : () => updateState({
                        sortId: item.id,
                        sortType: item.type,
                        sortDesc: selected ? !state.sortDesc : true
                      })}
                    >
                      {item.name}
                      {!isStatic && <i className={`ps-1 icon bi bi-${sortIcon}`} />}
                    </Label>
                    {filter && (
                      <i
                        id={item.id}
                        className={`ps-1 clickable icon bi bi-funnel-fill ${filterActive ? "link-success" : "link-secondary"}`}
                      />
                    )}
                  </div>
                  {filter && filterEnabled && (
                    <DismissablePopover
                      isOpen={popoverOpen}
                      toggle={() => updateState({ filterPopover: popoverOpen ? null : item.id })}
                      target={item.id}
                      placement="bottom"
                      popperClassName="full-width-popover"
                    >
                      <PopoverBody className="py-2 px-3 d-flex flex-column">
                        <div className="p-1" style={{ maxWidth: "400px", maxHeight: "400px", overflowX: "auto", whiteSpace:"nowrap" }}>
                          <Label className="fw-bold no-spacing">
                            Filter <i className="icon bi bi-funnel-fill" />
                          </Label>
                          <div className="d-flex">
                            <Input
                              type="checkbox"
                              checked={!filterActive}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                const newFilter = {};
                                Object.keys(filter).forEach(item => newFilter[item] = checked);
                                updateState({ enumFilter: { ...state.enumFilter, [filterKey]: newFilter } });
                              }}
                            />
                            <Label className="ps-1" check>Select All</Label>
                          </div>
                          {enumKeys.map((key, index) => {
                            let value = item.enum[key];
                            const selected = filter[key];
                            if (Array.isArray(value)) {
                              value = value.join(', ');
                            }

                            return (
                              <div key={`${tableName}-${index}-filter`} className="d-flex">
                                <Input
                                  className="flex-shrink-0"
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    const newFilter = { ...filter, [key]: checked };
                                    updateState({ enumFilter: { ...state.enumFilter, [filterKey]: newFilter } });
                                    onPageChanged?.(1);
                                  }}
                                />
                                <Label className="ps-1" check>{key == null ? "Unknown" : value}</Label>
                              </div>
                            );
                          })}
                        </div>
                      </PopoverBody>
                    </DismissablePopover>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {displayData.map((item, index) => {
            const itemSelectValue = item[selectKey];
            const selected = selectValue != null && itemSelectValue === selectValue;
            return (
              <tr
                key={`${tableName}${index}-data`}
                className="border-top no-select"
                onClick={() => onSelected?.(itemSelectValue)}
                style={{ backgroundColor: selected ? "#0161" : "#0000", cursor: onSelected == null ? "" : "pointer" }}
              >
                {checked !== null && 
                  <td key={`${tableName}${index}-checkbox`}>
                    <Input
                      type="checkbox"
                      checked={(item.checked ?? false) || checkedAll}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        onChecked?.(itemSelectValue, checked);
                      }}
                    />
                  </td>
                }
                {columns.map((column, columnIndex) => {
                  const value = convertValue(column.id, item[column.id], column.type)
                  const render = column.render ?? defaultRender;
                  return render({ index: columnIndex, data: item, value: value, id: column.id, rowIndex: index });
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
      {displayData.length === 0 && (
        <Label className="pt-3 w-100 text-center text-muted">{placeholder}</Label>
      )}
    </ScrollableDiv>
  );
};

export default SortableTable;
